import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, TrendingUp, Users, CheckCircle, Clock } from "lucide-react";
import { db } from "@/firebase/config";
import { collection, query, where, getDocs, orderBy, limit, onSnapshot } from "firebase/firestore";
import { Incident, DisciplineStats } from "./types";
import { formatDistanceToNow } from 'date-fns';

interface DisciplineDashboardProps {
  schoolId: string;
  onViewAll: () => void;
  onReportIncident: () => void;
}

export const DisciplineDashboard = ({ schoolId, onViewAll, onReportIncident }: DisciplineDashboardProps) => {
  const [stats, setStats] = useState<DisciplineStats>({
    totalIncidents: 0,
    openCases: 0,
    tier1Count: 0,
    tier2Count: 0,
    tier3Count: 0,
    thisWeekTrend: 0
  });
  const [recentIncidents, setRecentIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!schoolId) return;

    setLoading(true);
    const incidentsRef = collection(db, `schools/${schoolId}/disciplineIncidents`);
    
    // Real-time listener for stats
    const unsubscribe = onSnapshot(query(incidentsRef), (snapshot) => {
      let total = 0;
      let open = 0;
      let t1 = 0;
      let t2 = 0;
      let t3 = 0;

      const now = new Date();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      let thisWeekCount = 0;
      let lastWeekCount = 0; // Simplified trend logic

      snapshot.docs.forEach(doc => {
        const data = doc.data() as Incident;
        total++;
        if (data.status === 'new' || data.status === 'investigating') open++;
        if (data.tier === 1) t1++;
        if (data.tier === 2) t2++;
        if (data.tier === 3) t3++;

        const incidentDate = new Date(data.timestamp);
        if (incidentDate >= oneWeekAgo) thisWeekCount++;
      });

      setStats({
        totalIncidents: total,
        openCases: open,
        tier1Count: t1,
        tier2Count: t2,
        tier3Count: t3,
        thisWeekTrend: thisWeekCount // Showing raw count for now
      });
      setLoading(false);
    });

    // Fetch recent 5
    const recentQuery = query(incidentsRef, orderBy("timestamp", "desc"), limit(5));
    const unsubscribeRecent = onSnapshot(recentQuery, (snapshot) => {
      setRecentIncidents(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Incident)));
    });

    return () => {
      unsubscribe();
      unsubscribeRecent();
    };
  }, [schoolId]);

  const getSeverityColor = (severity: string) => {
    switch(severity) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'major': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      default: return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-muted-foreground">Loading discipline data...</div>;
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Open Cases</p>
              <h3 className="text-2xl font-bold">{stats.openCases}</h3>
            </div>
            <div className="h-10 w-10 bg-orange-100 dark:bg-orange-900/20 rounded-full flex items-center justify-center">
              <Clock className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Incidents (This Week)</p>
              <h3 className="text-2xl font-bold">{stats.thisWeekTrend}</h3>
            </div>
            <div className="h-10 w-10 bg-blue-100 dark:bg-blue-900/20 rounded-full flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Severe (Tier 3)</p>
              <h3 className="text-2xl font-bold">{stats.tier3Count}</h3>
            </div>
            <div className="h-10 w-10 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6 flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Resolved Total</p>
              <h3 className="text-2xl font-bold">{stats.totalIncidents - stats.openCases}</h3>
            </div>
            <div className="h-10 w-10 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Activity */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Recent Activity</CardTitle>
              <Button variant="outline" size="sm" onClick={onViewAll}>View All Cases</Button>
            </div>
            <CardDescription>Latest reported incidents and status updates.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {recentIncidents.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">No recent incidents recorded.</div>
              ) : (
                recentIncidents.map((incident) => (
                  <div key={incident.id} className="flex items-start gap-4 p-4 rounded-lg border border-slate-100 dark:border-slate-800 bg-white dark:bg-slate-950">
                     <div className={`mt-1 h-2 w-2 rounded-full flex-shrink-0 ${
                        incident.severity === 'critical' ? 'bg-red-500' : 
                        incident.severity === 'major' ? 'bg-orange-500' : 'bg-blue-500'
                     }`} />
                     <div className="flex-1 space-y-1">
                        <div className="flex items-center justify-between">
                           <p className="font-semibold text-sm">{incident.category} <span className="text-muted-foreground font-normal">involves</span> {incident.studentName}</p>
                           <Badge variant="outline" className={`${getSeverityColor(incident.severity)} border-0`}>
                             {incident.severity}
                           </Badge>
                        </div>
                        <p className="text-xs text-muted-foreground line-clamp-1">{incident.description}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground pt-1">
                           <span>{formatDistanceToNow(new Date(incident.timestamp))} ago</span>
                           <span>•</span>
                           <span className="capitalize">{incident.status.replace('_', ' ')}</span>
                        </div>
                     </div>
                  </div>
                ))
              )}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions & Tier Breakdown */}
        <div className="space-y-6">
           <Card className="bg-gradient-to-br from-indigo-500 to-purple-600 text-white border-0">
              <CardContent className="p-6">
                 <h3 className="text-lg font-bold mb-2">New Incident?</h3>
                 <p className="text-indigo-100 text-sm mb-4">Log a new behavioral observation, incident or positive merit.</p>
                 <Button onClick={onReportIncident} variant="secondary" className="w-full font-semibold text-indigo-700">
                    Report Incident
                 </Button>
              </CardContent>
           </Card>

           <Card>
              <CardHeader>
                 <CardTitle>Tier Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                 <div className="space-y-4">
                    <div className="flex items-center justify-between text-sm">
                       <span className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-green-500"></div> Tier 1 (Universal)</span>
                       <span className="font-bold">{stats.tier1Count}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                       <span className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-orange-500"></div> Tier 2 (Targeted)</span>
                       <span className="font-bold">{stats.tier2Count}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                       <span className="flex items-center gap-2"><div className="w-3 h-3 rounded-full bg-red-500"></div> Tier 3 (Intensive)</span>
                       <span className="font-bold">{stats.tier3Count}</span>
                    </div>
                 </div>
              </CardContent>
           </Card>
        </div>
      </div>
    </div>
  );
};
